import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Button, Chip, Grid, Card,
  CardContent, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Tooltip, LinearProgress, IconButton, Alert,
  FormControl, InputLabel, Select,
} from '@mui/material';
import {
  Schedule, Add, Edit, Delete, Close, CheckCircle, Warning,
  CalendarMonth, People, SwapHoriz,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

// ── API ───────────────────────────────────────────────────────────────────────

function apiFetch(path: string, opts: RequestInit = {}) {
  return fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:4000'}${path}`, {
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem('token')}`,
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || 'Error');
    return d;
  });
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SHIFT_TYPES: Record<string, { label: string; color: string }> = {
  morning:   { label: 'Morning',   color: '#f57c00' },
  afternoon: { label: 'Afternoon', color: '#1565c0' },
  evening:   { label: 'Evening',   color: '#6a1b9a' },
  night:     { label: 'Night',     color: '#1b2838' },
  flexible:  { label: 'Flexible',  color: '#2e7d32' },
  custom:    { label: 'Custom',    color: '#546e7a' },
};

const STATUS_COLORS: Record<string, string> = {
  scheduled: 'primary',
  confirmed: 'success',
  completed: 'default',
  absent:    'error',
  cancelled: 'error',
  swapped:   'warning',
};

function getWeekDates(weekStart: string): string[] {
  const dates: string[] = [];
  const d = new Date(weekStart);
  for (let i = 0; i < 7; i++) {
    dates.push(new Date(d.getTime() + i * 86400000).toISOString().slice(0, 10));
  }
  return dates;
}

function getMondayOfWeek(date = new Date()): string {
  const d = new Date(date);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function fmtDate(dateStr: string): string {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-HK', {
    weekday: 'short', month: 'short', day: 'numeric',
  });
}

// ── Types ─────────────────────────────────────────────────────────────────────

interface ShiftTemplate {
  id: string; name: string; type: string;
  startTime: string; endTime: string; breakMinutes: number;
  description: string; isActive: boolean;
}

interface Assignment {
  id: string; employeeId: string; shiftId: string; date: string;
  status: string; notes: string;
  shift?: ShiftTemplate;
  employee?: { firstName: string; lastName: string };
}

interface RosterEmployee {
  employeeId: string; employeeName: string; department: string;
  days: Record<string, Array<{ id: string; shiftName: string; startTime: string; endTime: string; status: string; }>>;
}

interface Stats { totalShifts: number; todayAssignments: number; weekAssignments: number; scheduledCount: number; }

// ── StatCard ──────────────────────────────────────────────────────────────────

function StatCard({ label, value, icon, color }: any) {
  return (
    <Card sx={{ textAlign: 'center', p: 1 }}>
      <CardContent>
        <Box sx={{ color, mb: 0.5 }}>{icon}</Box>
        <Typography variant="h4" fontWeight={700} color={color}>{value}</Typography>
        <Typography variant="body2" color="text.secondary">{label}</Typography>
      </CardContent>
    </Card>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function ShiftsPage() {
  const { user } = useAuth();
  const isHR = ['hr_admin', 'hr_manager'].includes(user?.role ?? '');
  const isManager = user?.role === 'manager';
  const canManage = isHR || isManager;

  const [tab, setTab] = useState(0);
  const [shifts, setShifts] = useState<ShiftTemplate[]>([]);
  const [mySchedule, setMySchedule] = useState<Assignment[]>([]);
  const [roster, setRoster] = useState<{ weekStart: string; weekEnd: string; employees: RosterEmployee[]; totalAssignments: number } | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [weekStart, setWeekStart] = useState(getMondayOfWeek());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Dialogs
  const [shiftDialog, setShiftDialog] = useState<Partial<ShiftTemplate> | null>(null);
  const [assignDialog, setAssignDialog] = useState<{
    employeeId: string; shiftId: string; date: string; notes: string;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<{ type: 'shift' | 'assignment'; id: string } | null>(null);

  // Employees for assignment picker (HR only)
  const [employees, setEmployees] = useState<any[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [s, my, r, st] = await Promise.all([
        apiFetch('/api/shifts/templates'),
        apiFetch(`/api/shifts/me?start=${weekStart}&end=${getWeekDates(weekStart)[6]}`),
        canManage ? apiFetch(`/api/shifts/roster?weekStart=${weekStart}`) : Promise.resolve(null),
        canManage ? apiFetch('/api/shifts/stats') : Promise.resolve(null),
      ]);
      setShifts(s);
      setMySchedule(my);
      setRoster(r);
      setStats(st);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [weekStart, canManage]);

  useEffect(() => { load(); }, [load]);

  // Load employees for assignment dialog
  useEffect(() => {
    if (canManage && employees.length === 0) {
      apiFetch('/api/employees?limit=500').then((r) => setEmployees(r.data || r || [])).catch(() => {});
    }
  }, [canManage, employees.length]);

  // ── Week navigation ──────────────────────────────────────────────────────────

  const prevWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() - 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  const nextWeek = () => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + 7);
    setWeekStart(d.toISOString().slice(0, 10));
  };

  // ── Shift CRUD ──────────────────────────────────────────────────────────────

  const saveShift = async () => {
    if (!shiftDialog) return;
    try {
      if (shiftDialog.id) {
        await apiFetch(`/api/shifts/templates/${shiftDialog.id}`, { method: 'PATCH', body: JSON.stringify(shiftDialog) });
      } else {
        await apiFetch('/api/shifts/templates', { method: 'POST', body: JSON.stringify(shiftDialog) });
      }
      setShiftDialog(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      if (deleteId.type === 'shift') {
        await apiFetch(`/api/shifts/templates/${deleteId.id}`, { method: 'DELETE' });
      } else {
        await apiFetch(`/api/shifts/assignments/${deleteId.id}`, { method: 'DELETE' });
      }
      setDeleteId(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Assignment ───────────────────────────────────────────────────────────────

  const saveAssignment = async () => {
    if (!assignDialog) return;
    try {
      await apiFetch('/api/shifts/assignments', { method: 'POST', body: JSON.stringify(assignDialog) });
      setAssignDialog(null);
      load();
    } catch (e: any) { setError(e.message); }
  };

  const updateStatus = async (id: string, status: string) => {
    try {
      await apiFetch(`/api/shifts/assignments/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
      load();
    } catch (e: any) { setError(e.message); }
  };

  // ── Roster grid days ─────────────────────────────────────────────────────────

  const weekDates = getWeekDates(weekStart);
  const today = new Date().toISOString().slice(0, 10);

  // ── Tabs ─────────────────────────────────────────────────────────────────────

  const tabs = [
    { label: 'My Schedule', show: true },
    { label: 'Weekly Roster', show: canManage },
    { label: 'Shift Templates', show: canManage },
  ].filter((t) => t.show);

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule sx={{ fontSize: 32, color: '#1565c0' }} />
          <Box>
            <Typography variant="h5" fontWeight={700}>Shift Scheduling</Typography>
            <Typography variant="body2" color="text.secondary">Manage rosters, shifts, and employee schedules</Typography>
          </Box>
        </Box>
        {canManage && (
          <Button variant="contained" startIcon={<Add />}
            onClick={() => setAssignDialog({ employeeId: '', shiftId: '', date: today, notes: '' })}>
            Assign Shift
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Stats (HR/manager) */}
      {canManage && stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Shift Types" value={stats.totalShifts} icon={<Schedule />} color="#1565c0" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Today's Shifts" value={stats.todayAssignments} icon={<CalendarMonth />} color="#2e7d32" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="This Week" value={stats.weekAssignments} icon={<People />} color="#7b1fa2" />
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <StatCard label="Scheduled" value={stats.scheduledCount} icon={<Warning />} color="#f57c00" />
          </Grid>
        </Grid>
      )}

      {/* Tabs */}
      <Paper sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)}>
          {tabs.map((t, i) => <Tab key={i} label={t.label} />)}
        </Tabs>
      </Paper>

      {loading && <LinearProgress sx={{ mb: 2 }} />}

      {/* Week Navigator */}
      {(tab === 0 || (canManage && tab === 1)) && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <Button onClick={prevWeek} size="small">← Prev</Button>
          <Typography fontWeight={600}>
            {fmtDate(weekStart)} — {fmtDate(weekDates[6])}
          </Typography>
          <Button onClick={nextWeek} size="small">Next →</Button>
          <Button size="small" onClick={() => setWeekStart(getMondayOfWeek())}>This Week</Button>
        </Box>
      )}

      {/* ── MY SCHEDULE ── */}
      {tab === 0 && (
        <Box>
          {mySchedule.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <CalendarMonth sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
              <Typography>No shifts scheduled for this week.</Typography>
            </Paper>
          ) : (
            <Grid container spacing={2}>
              {weekDates.map((date) => {
                const dayShifts = mySchedule.filter((a) => a.date === date || a.date?.startsWith(date));
                const isToday = date === today;
                return (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={date}>
                    <Card sx={{
                      border: isToday ? '2px solid #1565c0' : '1px solid #e0e0e0',
                      bgcolor: isToday ? '#e3f2fd' : 'white',
                    }}>
                      <CardContent sx={{ pb: '12px !important' }}>
                        <Typography variant="subtitle2" fontWeight={700} color={isToday ? 'primary' : 'text.primary'}>
                          {fmtDate(date)} {isToday && <Chip label="Today" size="small" color="primary" sx={{ ml: 0.5 }} />}
                        </Typography>
                        {dayShifts.length === 0 ? (
                          <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>Day off</Typography>
                        ) : (
                          dayShifts.map((a) => (
                            <Box key={a.id} sx={{ mt: 1, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="body2" fontWeight={600}>{a.shift?.name}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                {a.shift?.startTime} – {a.shift?.endTime}
                              </Typography>
                              <Box sx={{ mt: 0.5 }}>
                                <Chip
                                  label={a.status}
                                  size="small"
                                  color={(STATUS_COLORS[a.status] ?? 'default') as any}
                                />
                              </Box>
                            </Box>
                          ))
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      )}

      {/* ── WEEKLY ROSTER (HR/manager) ── */}
      {tab === 1 && canManage && roster && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {roster.totalAssignments} assignments this week
            </Typography>
            <Button size="small" startIcon={<Add />}
              onClick={() => setAssignDialog({ employeeId: '', shiftId: '', date: today, notes: '' })}>
              Add Shift
            </Button>
          </Box>
          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ minWidth: 150 }}><b>Employee</b></TableCell>
                  {weekDates.map((d) => (
                    <TableCell key={d} align="center" sx={{
                      minWidth: 110,
                      bgcolor: d === today ? '#e3f2fd' : undefined,
                      fontWeight: d === today ? 700 : 400,
                    }}>
                      {fmtDate(d)}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {roster.employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                      No assignments this week. Use "Add Shift" to create the roster.
                    </TableCell>
                  </TableRow>
                ) : (
                  roster.employees.map((emp) => (
                    <TableRow key={emp.employeeId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{emp.employeeName}</Typography>
                        <Typography variant="caption" color="text.secondary">{emp.department}</Typography>
                      </TableCell>
                      {weekDates.map((d) => {
                        const dayShifts = emp.days[d] ?? [];
                        return (
                          <TableCell key={d} align="center" sx={{ bgcolor: d === today ? '#f3f9ff' : undefined }}>
                            {dayShifts.map((s) => (
                              <Box key={s.id} sx={{ mb: 0.5 }}>
                                <Tooltip title={`${s.startTime}–${s.endTime} | ${s.status}`}>
                                  <Chip
                                    label={s.shiftName}
                                    size="small"
                                    color={(STATUS_COLORS[s.status] ?? 'default') as any}
                                    onDelete={() => setDeleteId({ type: 'assignment', id: s.id })}
                                    sx={{ fontSize: '0.65rem', maxWidth: 100 }}
                                  />
                                </Tooltip>
                              </Box>
                            ))}
                            {dayShifts.length === 0 && <Typography variant="caption" color="text.disabled">—</Typography>}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── SHIFT TEMPLATES (HR/manager) ── */}
      {tab === (canManage ? 2 : -1) && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<Add />}
              onClick={() => setShiftDialog({ type: 'custom', startTime: '09:00', endTime: '18:00', breakMinutes: 60, isActive: true })}>
              New Shift Type
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>Hours</b></TableCell>
                  <TableCell><b>Break</b></TableCell>
                  <TableCell><b>Duration</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shifts.map((s) => {
                  const [sh, sm] = s.startTime.split(':').map(Number);
                  const [eh, em] = s.endTime.split(':').map(Number);
                  let mins = (eh * 60 + em) - (sh * 60 + sm);
                  if (mins < 0) mins += 24 * 60;
                  const workMins = mins - s.breakMinutes;
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell><Typography fontWeight={600}>{s.name}</Typography></TableCell>
                      <TableCell>
                        <Chip
                          label={SHIFT_TYPES[s.type]?.label ?? s.type}
                          size="small"
                          sx={{ bgcolor: SHIFT_TYPES[s.type]?.color ?? '#90a4ae', color: '#fff', fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{s.startTime} – {s.endTime}</TableCell>
                      <TableCell>{s.breakMinutes}m</TableCell>
                      <TableCell>{Math.floor(workMins / 60)}h {workMins % 60 > 0 ? `${workMins % 60}m` : ''}</TableCell>
                      <TableCell>
                        <Chip label={s.isActive ? 'Active' : 'Inactive'} size="small" color={s.isActive ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setShiftDialog({ ...s })}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteId({ type: 'shift', id: s.id })}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Also show templates tab at index 2 */}
      {canManage && tab === 2 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="outlined" startIcon={<Add />}
              onClick={() => setShiftDialog({ type: 'custom', startTime: '09:00', endTime: '18:00', breakMinutes: 60, isActive: true })}>
              New Shift Type
            </Button>
          </Box>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell><b>Name</b></TableCell>
                  <TableCell><b>Type</b></TableCell>
                  <TableCell><b>Hours</b></TableCell>
                  <TableCell><b>Break</b></TableCell>
                  <TableCell><b>Description</b></TableCell>
                  <TableCell><b>Status</b></TableCell>
                  <TableCell><b>Actions</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shifts.map((s) => {
                  const [sh, sm] = s.startTime.split(':').map(Number);
                  const [eh, em] = s.endTime.split(':').map(Number);
                  let mins = (eh * 60 + em) - (sh * 60 + sm);
                  if (mins < 0) mins += 24 * 60;
                  const workMins = mins - s.breakMinutes;
                  return (
                    <TableRow key={s.id} hover>
                      <TableCell><Typography fontWeight={600}>{s.name}</Typography></TableCell>
                      <TableCell>
                        <Chip
                          label={SHIFT_TYPES[s.type]?.label ?? s.type}
                          size="small"
                          sx={{ bgcolor: SHIFT_TYPES[s.type]?.color ?? '#90a4ae', color: '#fff', fontSize: '0.7rem' }}
                        />
                      </TableCell>
                      <TableCell>{s.startTime} – {s.endTime}</TableCell>
                      <TableCell>{s.breakMinutes}m</TableCell>
                      <TableCell sx={{ maxWidth: 200 }}>
                        <Typography variant="body2" color="text.secondary" noWrap>{s.description || '—'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={s.isActive ? 'Active' : 'Inactive'} size="small" color={s.isActive ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => setShiftDialog({ ...s })}>
                            <Edit fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => setDeleteId({ type: 'shift', id: s.id })}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── SHIFT DIALOG ── */}
      <Dialog open={!!shiftDialog} onClose={() => setShiftDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {shiftDialog?.id ? 'Edit Shift Type' : 'New Shift Type'}
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setShiftDialog(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Shift Name" fullWidth required
              value={shiftDialog?.name ?? ''}
              onChange={(e) => setShiftDialog((p) => ({ ...p, name: e.target.value }))} />
            <TextField select label="Type" fullWidth
              value={shiftDialog?.type ?? 'custom'}
              onChange={(e) => setShiftDialog((p) => ({ ...p, type: e.target.value }))}>
              {Object.entries(SHIFT_TYPES).map(([k, v]) => (
                <MenuItem key={k} value={k}>{v.label}</MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Start Time" type="time" fullWidth
                value={shiftDialog?.startTime ?? '09:00'}
                onChange={(e) => setShiftDialog((p) => ({ ...p, startTime: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
              <TextField label="End Time" type="time" fullWidth
                value={shiftDialog?.endTime ?? '18:00'}
                onChange={(e) => setShiftDialog((p) => ({ ...p, endTime: e.target.value }))}
                InputLabelProps={{ shrink: true }} />
            </Box>
            <TextField label="Break (minutes)" type="number" fullWidth
              value={shiftDialog?.breakMinutes ?? 60}
              onChange={(e) => setShiftDialog((p) => ({ ...p, breakMinutes: Number(e.target.value) }))} />
            <TextField label="Description" fullWidth multiline rows={2}
              value={shiftDialog?.description ?? ''}
              onChange={(e) => setShiftDialog((p) => ({ ...p, description: e.target.value }))} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShiftDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveShift} disabled={!shiftDialog?.name}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* ── ASSIGN DIALOG ── */}
      <Dialog open={!!assignDialog} onClose={() => setAssignDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Assign Shift
          <IconButton sx={{ position: 'absolute', right: 8, top: 8 }} onClick={() => setAssignDialog(null)}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Employee</InputLabel>
              <Select
                value={assignDialog?.employeeId ?? ''}
                label="Employee"
                onChange={(e) => setAssignDialog((p) => p ? { ...p, employeeId: e.target.value } : null)}>
                {employees.map((emp: any) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                    {emp.department?.name ? ` — ${emp.department.name}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Shift</InputLabel>
              <Select
                value={assignDialog?.shiftId ?? ''}
                label="Shift"
                onChange={(e) => setAssignDialog((p) => p ? { ...p, shiftId: e.target.value } : null)}>
                {shifts.map((s) => (
                  <MenuItem key={s.id} value={s.id}>
                    {s.name} ({s.startTime}–{s.endTime})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Date" type="date" fullWidth
              value={assignDialog?.date ?? today}
              onChange={(e) => setAssignDialog((p) => p ? { ...p, date: e.target.value } : null)}
              InputLabelProps={{ shrink: true }} />
            <TextField label="Notes (optional)" fullWidth multiline rows={2}
              value={assignDialog?.notes ?? ''}
              onChange={(e) => setAssignDialog((p) => p ? { ...p, notes: e.target.value } : null)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveAssignment}
            disabled={!assignDialog?.employeeId || !assignDialog?.shiftId || !assignDialog?.date}>
            Assign
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── DELETE CONFIRM ── */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {deleteId?.type === 'shift'
              ? 'Delete this shift type? All existing assignments will also be removed.'
              : 'Remove this shift assignment?'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
